import { useEffect, useState } from 'react'
import { api, formatCurrencyFromCents, formatDate, formatDateTime, formatDuration } from '../../utils/api'
import { TrophyIcon, MoneyIcon, BoxesIcon, ZapIcon, CrownIcon } from './recordsIcons'

// Render a day's per-channel unit split as a quiet caption ("shopify 30 ·
// reverb 7"). Returns null when there is nothing to show so the caller can
// omit the line entirely.
function channelBreakdownCaption(breakdown) {
  if (!breakdown) return null
  const parts = Object.entries(breakdown).map(([channel, n]) => `${channel} ${n}`)
  return parts.length ? parts.join(' · ') : null
}

const noMotion = () =>
  typeof window === 'undefined' ||
  !window.matchMedia ||
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Animate a number from 0 to `end` on mount. Skipped when the user prefers
// reduced motion or matchMedia is unavailable (e.g. jsdom in tests): then the
// FIRST render already shows `end`, with no effect to wait for, so a reader of
// the page never sees a passing 0. Test: __tests__/useCountUp.test.jsx.
export function useCountUp(end, duration = 1200) {
  const reduce = noMotion()
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (reduce) return
    let raf
    const start = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration)
      const eased = 1 - Math.pow(1 - t, 3) // easeOutCubic
      setValue(t < 1 ? end * eased : end)
      if (t < 1) raf = requestAnimationFrame(step)
    }
    raf = requestAnimationFrame(step)
    return () => cancelAnimationFrame(raf)
  }, [end, duration, reduce])
  return reduce ? end : value
}

function CountUpValue({ end, format }) {
  const value = useCountUp(end)
  return <>{format(Math.round(value))}</>
}

export default function RecordsPanel() {
  const [records, setRecords] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let active = true
    async function load() {
      setLoading(true)
      try {
        const data = await api.get('/api/analytics/records')
        if (active) { setRecords(data); setError(null) }
      } catch (err) {
        if (active) setError(err.message || 'Failed to load records')
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [])

  if (loading) {
    return <div className="loading-center"><div className="loading-spinner" /></div>
  }
  if (error) {
    return (
      <div className="records-page">
        <div className="card records-error">Failed to load records: {error}</div>
      </div>
    )
  }

  const inbound = records?.inbound || {}
  const outbound = records?.outbound || {}
  const hero = [
    outbound.highest_revenue_day && {
      Icon: MoneyIcon,
      label: 'Highest revenue day',
      number: outbound.highest_revenue_day.revenue_cents,
      format: formatCurrencyFromCents,
      sub: formatDate(outbound.highest_revenue_day.date),
    },
    outbound.highest_units_sold_day && {
      Icon: BoxesIcon,
      label: 'Units sold day',
      number: outbound.highest_units_sold_day.units,
      format: (n) => `${n} units`,
      sub: formatDate(outbound.highest_units_sold_day.date),
      meta: channelBreakdownCaption(outbound.highest_units_sold_day.channel_breakdown),
    },
    outbound.most_expensive_item && {
      Icon: TrophyIcon,
      label: 'Most expensive item sold',
      number: outbound.most_expensive_item.amount_cents,
      format: formatCurrencyFromCents,
      sub: outbound.most_expensive_item.title,
      meta: formatDate(outbound.most_expensive_item.date),
    },
    inbound.busiest_quoting_day && {
      Icon: BoxesIcon,
      label: 'Busiest quoting day',
      number: inbound.busiest_quoting_day.item_count,
      format: (n) => `${n} items`,
      sub: formatDate(inbound.busiest_quoting_day.date),
    },
  ].filter(Boolean)

  // Distinct-tier cards below the hero wall. Quickest sale is a time metric,
  // not a money/count one, so it gets its own card (and the zap icon) rather
  // than sitting in the hero row.
  const distinct = [
    outbound.quickest_turnaround && {
      Icon: ZapIcon,
      label: 'Quickest sale',
      number: outbound.quickest_turnaround.turnaround_seconds,
      format: formatDuration,
      sub: outbound.quickest_turnaround.title,
      meta:
        `listed ${formatDateTime(outbound.quickest_turnaround.activated_at)} → ` +
        `sold ${formatDateTime(outbound.quickest_turnaround.sold_at)} ` +
        `on ${outbound.quickest_turnaround.channel}`,
    },
  ].filter(Boolean)

  const weekday = outbound.revenue_by_weekday || []
  const topWeekdayRev = weekday.length ? Math.max(...weekday.map((w) => w.revenue_cents)) : -1

  // C2B (trade-in) items-quoted leaderboard. Windowed to TI-tagged deals, so
  // the whole strip — heading included — is hidden until there is a coverage
  // start to label it honestly with.
  const c2bWeekday = inbound.c2b_items_by_weekday || []
  const c2bCoverageStart = inbound.c2b_coverage_start
  const topC2bItems = c2bWeekday.length ? Math.max(...c2bWeekday.map((w) => w.item_count)) : -1
  const showC2b = c2bWeekday.length > 0 && Boolean(c2bCoverageStart)

  const hasAny = hero.length > 0 || distinct.length > 0 || weekday.length > 0 || showC2b

  return (
    <div className="records-page">
      <div className="admin-header">
        <div>
          <h1>Hall of fame</h1>
          <p className="text-muted">Our all-time records</p>
        </div>
      </div>

      {!hasAny && <div className="card">No records yet — get selling!</div>}

      {hero.length > 0 && (
        <div className="records-hero">
          {hero.map((h) => (
            <div key={h.label} className="records-hero-card">
              <div className="records-hero-label">
                <h.Icon className="records-hero-icon" />
                {h.label}
              </div>
              <div className="records-hero-value">
                <CountUpValue end={h.number} format={h.format} />
              </div>
              <div className="records-hero-sub">{h.sub}</div>
              {h.meta && <div className="records-hero-meta">{h.meta}</div>}
            </div>
          ))}
        </div>
      )}

      {distinct.length > 0 && (
        <div className="records-grid">
          {distinct.map((d) => (
            <div key={d.label} className="records-card">
              <div className="records-card-label">
                <d.Icon className="records-card-icon" />
                {d.label}
              </div>
              <div className="records-card-value">
                <CountUpValue end={d.number} format={d.format} />
              </div>
              <div className="records-card-sub">{d.sub}</div>
              {d.meta && <div className="records-card-meta">{d.meta}</div>}
            </div>
          ))}
        </div>
      )}

      {weekday.length > 0 && (
        <>
          <h2 className="records-section-title">Biggest day by weekday</h2>
          <div className="records-weekday-grid">
            {weekday.map((w) => {
              const isTop = w.revenue_cents === topWeekdayRev
              return (
                <div
                  key={w.weekday}
                  className={`records-weekday-card${isTop ? ' is-top' : ''}`}
                >
                  <div className="records-weekday-name">
                    {isTop && <CrownIcon className="records-weekday-crown" />}
                    {w.weekday}
                  </div>
                  <div className="records-weekday-value">
                    {formatCurrencyFromCents(w.revenue_cents)}
                  </div>
                  <div className="text-muted text-sm">{formatDate(w.date)}</div>
                </div>
              )
            })}
          </div>
        </>
      )}

      {showC2b && (
        <>
          <h2 className="records-section-title">Most C2B items quoted</h2>
          <p className="records-section-sub text-muted">
            Trade-ins, since {formatDate(c2bCoverageStart)}
          </p>
          <div className="records-weekday-grid">
            {c2bWeekday.map((w) => {
              const isTop = w.item_count === topC2bItems
              return (
                <div
                  key={w.weekday}
                  className={`records-weekday-card${isTop ? ' is-top' : ''}`}
                >
                  <div className="records-weekday-name">
                    {isTop && <CrownIcon className="records-weekday-crown" />}
                    {w.weekday}
                  </div>
                  <div className="records-weekday-value">{w.item_count} items</div>
                  <div className="text-muted text-sm">{formatDate(w.date)}</div>
                </div>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}
